import { useEffect, useState } from 'react';
import { CheckCircle2, Copy, ExternalLink, Megaphone, Send, Undo2 } from 'lucide-react';
import {
  useDuplicatePost,
  useSetPostApproval,
  useSocialPost,
  useUpdatePost,
} from '../../hooks/useSocialPosts';
import { usePermissions } from '../../hooks/usePermissions';
import {
  APPROVAL_LABELS,
  APPROVAL_TONES,
  FORMAT_LABELS,
  PLATFORM_COLORS,
  PLATFORM_LABELS,
  SOCIAL_FORMATS,
  SOCIAL_PLATFORMS,
  type SocialPlatform,
  type SocialPostFormat,
} from '../../lib/socialPostsApi';
import { DateField } from '../ui/DateField';
import { TimeField } from '../ui/TimeField';
import { Select } from '../ui/Select';

const pad = (n: number) => String(n).padStart(2, '0');
function splitLocal(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}
function joinLocal(date: string, time: string): string {
  const [y, m, d] = date.split('-').map(Number);
  const [hh, mm] = (time || '12:00').split(':').map(Number);
  return new Date(y, m - 1, d, hh, mm).toISOString();
}

/**
 * The publishing side of a post, on its task page: platform, format, date and
 * time, caption, media link, the client's approval and its history. The task
 * itself (brief, assignee, status, deadline, comments) is the rest of the page.
 */
export function PostPanel({ taskId }: { taskId: string }) {
  const { data: post } = useSocialPost(taskId);
  const update = useUpdatePost(taskId);
  const approval = useSetPostApproval(taskId);
  const duplicate = useDuplicatePost(taskId);
  const { can } = usePermissions();
  const canEdit = can('socialPosts', 'canEdit');
  const canCreate = can('socialPosts', 'canCreate');

  const [caption, setCaption] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [dupPlatform, setDupPlatform] = useState('');
  useEffect(() => {
    if (post) {
      setCaption(post.caption ?? '');
      setMediaUrl(post.mediaUrl ?? '');
    }
  }, [post]);

  if (!post) return null;
  const when = splitLocal(post.publishAt);
  const validUrl = /^https?:\/\/\S+$/i.test(mediaUrl.trim());

  return (
    <section className="mt-4 rounded-lg border border-gray-200 bg-card p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Megaphone size={16} className="text-gray-500" />
        <h3 className="text-sm font-semibold text-gray-900">Postare</h3>
        <span
          className="rounded-full px-2 py-0.5 text-xs font-medium text-fixed-white"
          style={{ backgroundColor: PLATFORM_COLORS[post.platform] }}
        >
          {PLATFORM_LABELS[post.platform]}
        </span>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${APPROVAL_TONES[post.approvalStatus]}`}
        >
          {APPROVAL_LABELS[post.approvalStatus]}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Select
          label="Platformă"
          value={post.platform}
          disabled={!canEdit}
          onChange={(v) => update.mutate({ platform: v as SocialPlatform })}
          options={SOCIAL_PLATFORMS.map((p) => ({ value: p, label: PLATFORM_LABELS[p] }))}
        />
        <Select
          label="Format"
          value={post.format}
          disabled={!canEdit}
          onChange={(v) => update.mutate({ format: v as SocialPostFormat })}
          options={SOCIAL_FORMATS.map((f) => ({ value: f, label: FORMAT_LABELS[f] }))}
        />
        <label className="flex flex-col text-sm font-medium text-gray-700">
          Ziua publicării
          <DateField
            value={when.date}
            required
            disabled={!canEdit}
            onChange={(d) => d && update.mutate({ publishAt: joinLocal(d, when.time) })}
            className="mt-1 w-full"
          />
        </label>
        <label className="flex flex-col text-sm font-medium text-gray-700">
          Ora
          <TimeField
            value={when.time}
            step={15}
            required
            disabled={!canEdit}
            onChange={(t) => t && update.mutate({ publishAt: joinLocal(when.date, t) })}
            className="mt-1 w-full"
          />
        </label>
        <label className="flex flex-col text-sm font-medium text-gray-700 sm:col-span-2">
          Textul postării
          <textarea
            value={caption}
            readOnly={!canEdit}
            onChange={(e) => setCaption(e.target.value)}
            onBlur={() => {
              if (caption !== (post.caption ?? '')) update.mutate({ caption: caption || null });
            }}
            rows={4}
            className="ui-textarea mt-1"
          />
        </label>
        <label className="flex flex-col text-sm font-medium text-gray-700 sm:col-span-2">
          Link video / material
          <div className="mt-1 flex gap-2">
            <input
              type="url"
              value={mediaUrl}
              readOnly={!canEdit}
              onChange={(e) => setMediaUrl(e.target.value)}
              onBlur={() => {
                const next = mediaUrl.trim();
                if (next !== (post.mediaUrl ?? '') && (next === '' || validUrl)) {
                  update.mutate({ mediaUrl: next || null });
                }
              }}
              placeholder="https://…"
              className="ui-input w-full"
            />
            {validUrl && (
              <a
                href={mediaUrl.trim()}
                target="_blank"
                rel="noreferrer"
                className="inline-flex shrink-0 items-center gap-1 rounded-md border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50"
              >
                <ExternalLink size={14} /> Deschide video
              </a>
            )}
          </div>
        </label>
      </div>

      {canEdit && (
        <div className="mt-4 flex flex-wrap gap-2">
          {post.approvalStatus !== 'PENDING_CLIENT' && (
            <button
              type="button"
              disabled={approval.isPending}
              onClick={() => approval.mutate({ status: 'PENDING_CLIENT' })}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              <Send size={14} /> Trimite la client
            </button>
          )}
          {post.approvalStatus !== 'APPROVED' && (
            <button
              type="button"
              disabled={approval.isPending}
              onClick={() => approval.mutate({ status: 'APPROVED' })}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              <CheckCircle2 size={14} /> Marchează aprobată
            </button>
          )}
          {post.approvalStatus !== 'DRAFT' && (
            <button
              type="button"
              disabled={approval.isPending}
              onClick={() => approval.mutate({ status: 'DRAFT' })}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              <Undo2 size={14} /> Înapoi la ciornă
            </button>
          )}
        </div>
      )}

      {canCreate && (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <div className="w-48">
            <Select
              label="Duplică pe altă platformă"
              value={dupPlatform}
              onChange={setDupPlatform}
              placeholder="Alege platforma"
              options={SOCIAL_PLATFORMS.filter((p) => p !== post.platform).map((p) => ({
                value: p,
                label: PLATFORM_LABELS[p],
              }))}
            />
          </div>
          <button
            type="button"
            disabled={!dupPlatform || duplicate.isPending}
            onClick={() =>
              duplicate.mutate(
                { platform: dupPlatform as SocialPlatform },
                { onSuccess: () => setDupPlatform('') },
              )
            }
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <Copy size={14} /> Duplică
          </button>
          {duplicate.isSuccess && (
            <span className="text-xs text-green-700">Copia a fost creată.</span>
          )}
        </div>
      )}

      {post.approvals.length > 0 && (
        <div className="mt-4 border-t border-gray-100 pt-3">
          <h4 className="text-xs font-medium uppercase tracking-wide text-gray-400">
            Istoric aprobare
          </h4>
          <ul className="mt-2 space-y-2">
            {post.approvals.map((a) => (
              <li key={a.id} className="text-sm">
                <span className="font-medium text-gray-900">{a.actorName}</span>
                {a.viaShareLink && <span className="text-gray-400"> (client, din link)</span>}
                <span className="text-gray-600"> · {APPROVAL_LABELS[a.status]}</span>
                <span className="text-xs text-gray-400">
                  {' '}
                  · {new Date(a.createdAt).toLocaleString('ro-RO')}
                </span>
                {a.comment && (
                  <p className="mt-0.5 whitespace-pre-wrap text-gray-700">{a.comment}</p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
